/**
 * Metadata hooks — thin adapter layer.
 *
 * Canonical implementations live in @atlas-int/data.
 * This file injects the app-specific `servicesClass` URL parameter
 * into hooks that need it. For all other hooks and utilities,
 * import directly from "@atlas-int/data".
 */

import { useMemo } from "react";
import { useExploreParams } from "../visualization/paramSchemas";
import type { ProductClass } from "@atlas-int/data/types";
import type { MetadataFetchState } from "@atlas-int/data/types";
import {
  useProductsMetadata as _useProductsMetadata,
  createProductLookupIndex,
  createProductHierarchyTree,
} from "@atlas-int/data";

// ── Wrapped hooks (inject servicesClass from URL) ───────────────

export function useProductsMetadata(productClass?: ProductClass) {
  const [{ servicesClass }] = useExploreParams();
  return _useProductsMetadata(productClass, { servicesClass });
}

/**
 * Product lookup index with servicesClass injection.
 * Wraps useProductsMetadata (which needs URL params) + createProductLookupIndex.
 */
export function useProductLookupIndex(productClass: ProductClass): MetadataFetchState<ReturnType<typeof createProductLookupIndex>> {
  const productsState = useProductsMetadata(productClass);
  const data = productsState.status === "success" ? productsState.data : undefined;

  const lookup = useMemo(() => {
    if (!data) return undefined;
    const allProducts = [
      ...data.section,
      ...data.twoDigit,
      ...data.fourDigit,
      ...data.sixDigit,
    ];
    return createProductLookupIndex(allProducts);
  }, [data]);

  if (productsState.status === "error") return { status: "error", error: productsState.error };
  if (productsState.status === "loading") return { status: "loading" };
  if (lookup !== undefined) return { status: "success", data: lookup };
  return { status: "idle" };
}

/**
 * Product hierarchy tree with servicesClass injection.
 */
export function useProductHierarchyTree(productClass: ProductClass): MetadataFetchState<ReturnType<typeof createProductHierarchyTree>> {
  const productsState = useProductsMetadata(productClass);
  const data = productsState.status === "success" ? productsState.data : undefined;

  const tree = useMemo(() => {
    if (!data) return createProductHierarchyTree([]);
    const allProducts = [
      ...data.section,
      ...data.twoDigit,
      ...data.fourDigit,
      ...data.sixDigit,
    ];
    return createProductHierarchyTree(allProducts);
  }, [data]);

  if (productsState.status === "error") return { status: "error", error: productsState.error };
  if (productsState.status === "loading") return { status: "loading" };
  if (tree !== undefined) return { status: "success", data: tree };
  return { status: "idle" };
}
